import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  Button,
  FormFeedback,
  Alert,
  Spinner,
} from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import PageGuard from '../../components/Common/PageGuard';
import { GET_USER, GetUserData, GetUserVariables } from '../../graphql/queries/users';
import { UPDATE_USER, UpdateUserVariables } from '../../graphql/mutations/users';
import { useAuth } from '../../contexts/AuthContext';
import { User } from '../../graphql/queries/users/types';
import { resetUserTotp } from '../../helpers/wordpress_totp';

const UsersEdit: React.FC = () => {
  document.title = 'Edit User | NGK Tours';

  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isSuperAdmin } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [totpMessage, setTotpMessage] = useState<string | null>(null);
  const [resettingTotp, setResettingTotp] = useState(false);

  // Fetch user details
  const { data, loading: loadingUser, error: loadError } = useQuery<GetUserData, GetUserVariables>(
    GET_USER,
    {
      skip: !id,
      variables: { id: id as string },
      fetchPolicy: 'network-only',
    }
  );

  const user: User | undefined = data?.user;

  const availableRoles = useMemo(
    () =>
      isSuperAdmin()
        ? [
            { value: 'super_admin_business', label: 'Super Admin Business' },
            { value: 'admin_business', label: 'Admin Business' },
            { value: 'reservation_business', label: 'Reservation Business' },
            { value: 'account_business', label: 'Account Business' },
            { value: 'operations_business', label: 'Operations Business' },
            { value: 'agent_business', label: 'Agent Business' },
          ]
        : [
            { value: 'admin_business', label: 'Admin Business' },
            { value: 'reservation_business', label: 'Reservation Business' },
            { value: 'account_business', label: 'Account Business' },
            { value: 'operations_business', label: 'Operations Business' },
            { value: 'agent_business', label: 'Agent Business' },
          ],
    [isSuperAdmin]
  );

  // Derive current primary business role for select
  const currentRoles = user?.roles?.nodes?.map((r) => r.name) || [];
  const primaryBusinessRole =
    currentRoles.find((r) => r.includes('business')) || currentRoles[0] || '';

  const [updateUser, { loading: saving }] = useMutation<unknown, UpdateUserVariables>(
    UPDATE_USER,
    {
      onCompleted: () => {
        navigate('/users-list');
      },
      onError: (err) => {
        setError(err.message || 'Failed to update user');
      },
    }
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: user?.email || '',
      role: primaryBusinessRole || '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().email('Invalid email').required('Email is required'),
      role: Yup.string().required('Role is required'),
      password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .nullable()
        .transform((value) => (value === '' ? null : value)),
      confirmPassword: Yup.string().when('password', {
        is: (val: string | null) => !!val,
        then: (schema) =>
          schema
            .required('Please confirm the new password')
            .oneOf([Yup.ref('password')], 'Passwords must match'),
        otherwise: (schema) => schema.optional(),
      }),
    }),
    onSubmit: (values) => {
      if (!id) return;

      setError(null);

      // Preserve all existing roles except business_* roles, then add selected business role
      const nonBusinessRoles = currentRoles.filter((r) => !r.includes('business'));
      const newRoles = values.role ? [...nonBusinessRoles, values.role] : nonBusinessRoles;

      const input: any = {
        id: id,
        email: values.email,
        roles: newRoles,
      };

      if (values.password) {
        input.password = values.password;
      }

      updateUser({
        variables: {
          input,
        },
      });
    },
  });

  if (loadingUser) {
    return (
      <PageGuard requiredCapabilities={['edit_users']}>
        <div className="page-content">
          <Container fluid>
            <Row>
              <Col lg="12">
                <Card>
                  <CardBody className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-3 mb-0">Loading user...</p>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </PageGuard>
    );
  }

  if (loadError || !user) {
    return (
      <PageGuard requiredCapabilities={['edit_users']}>
        <div className="page-content">
          <Container fluid>
            <Row>
              <Col lg="12">
                <Card>
                  <CardBody>
                    <Alert color="danger" className="mb-3">
                      <h5 className="mb-2">Error loading user</h5>
                      <p className="mb-0">{loadError?.message || 'User not found'}</p>
                    </Alert>
                    <Button color="primary" onClick={() => navigate('/users-list')}>
                      Back to Users List
                    </Button>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </PageGuard>
    );
  }

  return (
    <PageGuard requiredCapabilities={['edit_users']}>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Users" breadcrumbItem="Edit User" />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <h4 className="card-title mb-4">Edit User</h4>

                  {error && (
                    <Alert color="danger" className="mb-3">
                      {error}
                    </Alert>
                  )}

                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                      return false;
                    }}
                  >
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label>Username</Label>
                          <Input
                            type="text"
                            value={user.username}
                            readOnly
                            disabled
                          />
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="mb-3">
                          <Label>Email *</Label>
                          <Input
                            name="email"
                            type="email"
                            placeholder="Enter email"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email}
                            invalid={!!(validation.touched.email && validation.errors.email)}
                          />
                          {validation.touched.email && validation.errors.email && (
                            <FormFeedback>{validation.errors.email}</FormFeedback>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label>Role *</Label>
                          <Input
                            name="role"
                            type="select"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.role}
                            invalid={!!(validation.touched.role && validation.errors.role)}
                          >
                            <option value="">Select a role</option>
                            {availableRoles.map((role) => (
                              <option key={role.value} value={role.value}>
                                {role.label}
                              </option>
                            ))}
                          </Input>
                          {validation.touched.role && validation.errors.role && (
                            <FormFeedback>{validation.errors.role}</FormFeedback>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label>New Password</Label>
                          <Input
                            name="password"
                            type="password"
                            placeholder="Leave blank to keep current password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.password}
                            invalid={!!(validation.touched.password && validation.errors.password)}
                          />
                          {validation.touched.password && validation.errors.password && (
                            <FormFeedback>{validation.errors.password as string}</FormFeedback>
                          )}
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="mb-3">
                          <Label>Confirm New Password</Label>
                          <Input
                            name="confirmPassword"
                            type="password"
                            placeholder="Re-enter new password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.confirmPassword}
                            invalid={
                              !!(
                                validation.touched.confirmPassword &&
                                validation.errors.confirmPassword
                              )
                            }
                          />
                          {validation.touched.confirmPassword &&
                            validation.errors.confirmPassword && (
                              <FormFeedback>
                                {validation.errors.confirmPassword as string}
                              </FormFeedback>
                            )}
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12}>
                        <div className="mb-3 p-3 border rounded">
                          <Label className="mb-1">Authenticator 2FA</Label>
                          <p className="text-muted small mb-2">
                            Status:{' '}
                            <strong>
                              {user.totpEnabled ? 'Enabled' : 'Not enabled'}
                            </strong>
                          </p>
                          {isSuperAdmin() && (
                            <Button
                              type="button"
                              color="warning"
                              size="sm"
                              disabled={resettingTotp || !user.totpEnabled}
                              onClick={async () => {
                                if (
                                  !window.confirm(
                                    'Reset authenticator 2FA for this user? They must set it up again on next login.'
                                  )
                                ) {
                                  return;
                                }
                                setTotpMessage(null);
                                setError(null);
                                setResettingTotp(true);
                                try {
                                  await resetUserTotp(String(user.databaseId));
                                  setTotpMessage(
                                    'Authenticator 2FA reset. User must enroll again on next login.'
                                  );
                                } catch (err: any) {
                                  setError(err?.message || 'Failed to reset 2FA');
                                } finally {
                                  setResettingTotp(false);
                                }
                              }}
                            >
                              {resettingTotp ? 'Resetting...' : 'Reset 2FA (Super Admin)'}
                            </Button>
                          )}
                          {totpMessage && (
                            <Alert color="success" className="mt-2 mb-0">
                              {totpMessage}
                            </Alert>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <div className="d-flex gap-2 justify-content-end mt-4">
                      <Button
                        type="button"
                        color="secondary"
                        onClick={() => navigate('/users-list')}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" color="primary" disabled={saving}>
                        {saving ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Saving...
                          </>
                        ) : (
                          'Save Changes'
                        )}
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </PageGuard>
  );
};

export default UsersEdit;

